/**
 * Splitting a joined customer name into its parts.
 *
 * A check-in session stores the customer's given and family name separately;
 * `customerName` is derived from them server-side. Several staff-side sources
 * still only offer one string — the customer-lookup index, and the WorkItem a
 * return or drop-off is prefilled from — so those get split here.
 *
 * Mirrors `split_full_name` in sm4rt-api's `core/services/check_in_names.py`,
 * which is what the server uses for the same job. Keep them in step: a
 * disagreement about where the boundary sits shows up as the name changing
 * when a session is saved.
 */

export interface CustomerNameParts {
  customerFirstName: string;
  customerLastName: string;
}

/**
 * `"Jane Smith"` -> `{ first: "Jane", last: "Smith" }`, splitting on the LAST
 * space. A single word is taken as a first name with no surname.
 *
 * Multi-word given names lose the boundary ("Mary Jane Smith" -> "Mary Jane" /
 * "Smith"). That is why this is only ever used to PREFILL two editable fields
 * — reception can see the split and correct it before saving.
 */
export const splitFullName = (full: string | null | undefined): CustomerNameParts => {
  const cleaned = (full ?? '').trim().replace(/\s+/g, ' ');
  if (!cleaned) return { customerFirstName: '', customerLastName: '' };
  const cut = cleaned.lastIndexOf(' ');
  if (cut < 0) return { customerFirstName: cleaned, customerLastName: '' };
  return {
    customerFirstName: cleaned.slice(0, cut),
    customerLastName: cleaned.slice(cut + 1),
  };
};

/**
 * The parts to show for a record that may or may not carry them yet. Prefers
 * the stored parts; falls back to splitting the joined name for sessions that
 * predate the split.
 */
export const nameParts = (
  first: string | null | undefined,
  last: string | null | undefined,
  joined: string | null | undefined,
): CustomerNameParts =>
  first || last
    ? { customerFirstName: first ?? '', customerLastName: last ?? '' }
    : splitFullName(joined);
